'use client'

import { useState, useEffect, useMemo } from 'react'
import { useSearchParams, useRouter, usePathname } from 'next/navigation'
import { cn, buildUrl } from '@/shared/lib/utils'
import type { Group } from '@/features/stm/lib/types'
import { GroupDashboardTab } from './GroupDashboardTab'
import { GroupParticipantsTab } from './GroupParticipantsTab'
import { GroupPreTripTab } from './GroupPreTripTab'
import GroupPhotosTimelineTab from './GroupPhotosTimelineTab'
import {
  SEARCH_PARAMS_RETURN_GROUP_ID_KEY,
  SEARCH_PARAMS_WEEK_START_KEY,
} from '@/features/volunteers/constants/volunteer-tab-links'

const GROUP_DETAIL_TABS = [
  { label: 'Dashboard', value: 'dashboard' as const },
  { label: 'Volunteers', value: 'members' as const },
  { label: 'PreTrip', value: 'pretrip' as const },
  { label: 'Photos', value: 'photos' as const },
] as const

const MEMBER_ONLY_TABS = [
  { label: 'Volunteers', value: 'members' as const },
  { label: 'PreTrip', value: 'pretrip' as const },
  { label: 'Photos', value: 'photos' as const },
] as const

type GroupTabValue = (typeof GROUP_DETAIL_TABS)[number]['value']
type MemberOnlyTabValue = (typeof MEMBER_ONLY_TABS)[number]['value']

type GroupDetailsTabsProps = {
  group: Group
  regionId: number | null
  restrictToPreTripAndPhotos?: boolean
}

export function GroupDetailsTabs({ group, regionId, restrictToPreTripAndPhotos = false }: GroupDetailsTabsProps) {
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()

  const tabs = restrictToPreTripAndPhotos ? MEMBER_ONLY_TABS : GROUP_DETAIL_TABS

  const fromParam = searchParams.get('from') ?? undefined
  const weekStartParam = searchParams.get(SEARCH_PARAMS_WEEK_START_KEY) ?? undefined
  const returnToGroupIdParam = searchParams.get(SEARCH_PARAMS_RETURN_GROUP_ID_KEY) ?? undefined
  const previousParams = useMemo(
    () => ({
      from: fromParam,
      [SEARCH_PARAMS_WEEK_START_KEY]: weekStartParam,
      [SEARCH_PARAMS_RETURN_GROUP_ID_KEY]: returnToGroupIdParam,
    }),
    [fromParam, weekStartParam, returnToGroupIdParam]
  )

  const getInitialTab = (): GroupTabValue | MemberOnlyTabValue => {
    const tabParam = searchParams.get('tab')
    if (tabParam === 'dashboard' || tabParam === 'members' || tabParam === 'pretrip' || tabParam === 'photos') {
      if (restrictToPreTripAndPhotos && tabParam === 'dashboard') return 'pretrip'
      return tabParam
    }
    return restrictToPreTripAndPhotos ? 'pretrip' : 'dashboard'
  }

  const [activeTab, setActiveTab] = useState<GroupTabValue | MemberOnlyTabValue>(getInitialTab())

  useEffect(() => {
    const tabParam = searchParams.get('tab')
    if (tabParam === 'dashboard' || tabParam === 'members' || tabParam === 'pretrip' || tabParam === 'photos') {
      if (restrictToPreTripAndPhotos && tabParam === 'dashboard') {
        setActiveTab('pretrip')
        const url = buildUrl(pathname, { ...previousParams, tab: 'pretrip' })
        router.replace(url)
        return
      }
      setActiveTab(tabParam)
    } else {
      const defaultTab = restrictToPreTripAndPhotos ? 'pretrip' : 'dashboard'
      setActiveTab(defaultTab)
      if (restrictToPreTripAndPhotos) {
        const url = buildUrl(pathname, { ...previousParams, tab: 'pretrip' })
        router.replace(url)
      }
    }
  }, [searchParams, restrictToPreTripAndPhotos, pathname, router, previousParams])

  const handleTabChange = (tabValue: GroupTabValue | MemberOnlyTabValue) => {
    setActiveTab(tabValue)
    const url = buildUrl(pathname, { ...previousParams, tab: tabValue })
    router.replace(url)
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-center gap-5">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.value
          return (
            <button
              key={tab.value}
              type="button"
              onClick={() => handleTabChange(tab.value)}
              className={cn(
                'w-[100px] text-center pb-0.5 border-b-[3px] border-transparent',
                isActive && 'border-primary text-primary font-medium'
              )}
            >
              {tab.label}
            </button>
          )
        })}
      </div>

      <div className="mt-6">
        {activeTab === 'dashboard' && <GroupDashboardTab group={group} />}
        {activeTab === 'members' && <GroupParticipantsTab group={group} isVolunteerView={restrictToPreTripAndPhotos} />}
        {activeTab === 'pretrip' && <GroupPreTripTab group={group} regionId={regionId} />}
        {activeTab === 'photos' && (
          <GroupPhotosTimelineTab groupId={group.id} />
        )}
      </div>
    </div>
  )
}
